import { useEffect, useState } from "react";
import { Columns3, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const MIN_W = 70;

const read = (key, fallback) => {
  try { return { ...fallback, ...JSON.parse(localStorage.getItem(key) || "{}") }; } catch (e) { return fallback; }
};

export const useColumnPrefs = (storageKey, columns) => {
  const defW = Object.fromEntries(columns.map((c) => [c.key, c.width]));
  const defV = Object.fromEntries(columns.map((c) => [c.key, true]));
  const [widths, setWidths] = useState(() => read(`${storageKey}:w`, defW));
  const [visible, setVisible] = useState(() => read(`${storageKey}:v`, defV));
  useEffect(() => { localStorage.setItem(`${storageKey}:w`, JSON.stringify(widths)); }, [widths, storageKey]);
  useEffect(() => { localStorage.setItem(`${storageKey}:v`, JSON.stringify(visible)); }, [visible, storageKey]);
  const toggle = (k) => setVisible((s) => ({ ...s, [k]: !s[k] }));
  const resize = (k, w) => setWidths((s) => ({ ...s, [k]: Math.max(MIN_W, Math.round(w)) }));
  const reset = () => { setWidths(defW); setVisible(defV); };
  return { widths, visible, toggle, resize, reset, shown: columns.filter((c) => visible[c.key]) };
};

export const ResizeHandle = ({ colKey, width, onResize }) => {
  const start = (e) => {
    e.preventDefault();
    const x0 = e.clientX;
    const move = (ev) => onResize(colKey, width + ev.clientX - x0);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); document.body.style.cursor = ""; };
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <span role="separator" aria-label="Ajustar ancho de columna" onPointerDown={start} data-testid={`col-resize-${colKey}`}
      className="group absolute right-0 top-0 z-10 flex h-full w-3 cursor-col-resize touch-none select-none justify-center">
      <span className="h-full w-px bg-slate-200 transition-colors group-hover:w-0.5 group-hover:bg-[#2F5BFF]" />
    </span>
  );
};

export const ColumnPicker = ({ columns, prefs }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" data-testid="btn-columns-picker"><Columns3 size={16} className="mr-2" />Columnas</Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuLabel>Columnas visibles</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {columns.map((c) => (
        <DropdownMenuCheckboxItem key={c.key} checked={!!prefs.visible[c.key]} onSelect={(e) => e.preventDefault()}
          disabled={prefs.visible[c.key] && prefs.shown.length === 1} onCheckedChange={() => prefs.toggle(c.key)} data-testid={`col-toggle-${c.key}`}>
          {c.label}
        </DropdownMenuCheckboxItem>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={prefs.reset} data-testid="col-reset"><RotateCcw size={14} className="mr-2" />Restablecer columnas</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
